import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Group,
  NavLink,
  Text,
  Avatar,
  Menu,
  UnstyledButton,
  Stack,
  Divider,
  Box,
  useMantineTheme,
  Indicator,
  Badge,
  Popover,
  Button,
  ThemeIcon,
  Tooltip,
  Collapse,
  ScrollArea,
  Alert,
  ActionIcon,
  Kbd,
  SimpleGrid,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconUsers,
  IconAlertTriangle,
  IconLogout,
  IconRefresh,
  IconChevronDown,
  IconChevronRight,
  IconPlayerPause,
  IconExternalLink,
  IconAlertCircle,
  IconClock,
  IconSearch,
  IconStar,
  IconStarFilled,
  IconHistory,
  IconBolt,
  IconSparkles,
  IconLayoutSidebarLeftCollapse,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth';
import { personColor, personRingStyle } from '../../lib/personColors';
import { usePersonColors } from '../../hooks/usePersonColors';
import { CustomerSsoSetupBanner } from '../CustomerSsoSetupBanner';
import { MailIntakeSetupBanner } from '../MailIntakeSetupBanner';
import { ViewAsBanner } from '../viewAs/ViewAsBanner';
import { getPortalRoleLabel } from '../../lib/rbac';
import { api, Submission } from '../../lib/api';
import { BottomNav } from './BottomNav';
import { OfflineIndicator } from '../OfflineIndicator';
import { PendingWeclappWorkIndicator } from '../PendingWeclappWorkIndicator';
import { TimeProposalIndicator } from '../TimeProposalIndicator';
import { AttendanceHeaderButton } from '../attendance/AttendanceHeaderButton';
import { WeclappTokenIndicator } from '../WeclappTokenIndicator';
import { NotificationBell } from './NotificationBell';
import { OfflineInfoModal } from '../OfflineInfoModal';
import { ChangelogModal, openChangelog } from '../onboarding/ChangelogModal';
import { TodoInboxModal } from './TodoInboxModal';
import { ConflictResolutionDialog } from '../ConflictResolutionDialog';
import { SyncErrorDialog } from '../SyncErrorDialog';
import { useSync } from '../../hooks/useSync';
import { useNavPreferences } from '../../hooks/useNavPreferences';
import { useNavBadges } from '../../hooks/useNavBadges';
import { useCompactNav } from '../../hooks/useCompactNav';
import { ACTIVE_HUB_QUERY_KEY } from '../../hooks/useActiveHub';
import { CommandPalette } from './CommandPalette';
import { CompactNavRail } from './CompactNavRail';
import { NAV_QUICK_ACTIONS, NavCountBadge, navBadgeCount } from './navShared';
import {
  getNavGroups,
  getAllLeaves,
  findLeafByPath,
  findActiveGroupId,
  isLeafActive,
  QUICK_LINKS,
  QUICK_LINK_BASE_PATHS,
  type NavGroup,
  type NavLeaf,
} from './navConfig';
import { GuidedTour } from '../onboarding/GuidedTour';
import styles from './AppLayout.module.css';

const NAV_WIDTH = 260;
const NAV_RAIL_WIDTH = 64;

export function AppLayout() {
  const [opened, setOpened] = useState(false);
  const [parkedPopoverOpen, setParkedPopoverOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const { syncNow } = useSync();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMantineTheme();
  const { user, logout } = useAuthStore();
  // Aus der Technikerliste, damit eine in der Benutzerverwaltung geänderte Farbe sofort greift;
  // bis sie geladen ist, die aus /auth/me.
  const personColors = usePersonColors();
  const myColor = personColors.knows(user?.id) ? personColors.colorOf(user?.id) : personColor(user?.color, user?.id);
  const isAdmin = user?.role === 'ADMIN';
  const isTeamlead = user?.role === 'TEAMLEAD';

  const { favorites, isFavorite, toggleFavorite, recents, recordRecent } = useNavPreferences();
  const navBadges = useNavBadges();

  // Schmale Leiste nur ab `sm`: darunter ist die Navbar der Vollbild-Drawer hinter dem Burger
  // und zeigt immer das volle Menü. Wert gleich beim ersten Rendern, sonst klappte die Navbar
  // bei jedem Laden sichtbar von 260px auf die Leiste zusammen.
  const isDesktop = useMediaQuery('(min-width: 48em)', false, { getInitialValueInEffect: false });
  const { compact: compactPreferred, toggle: toggleCompact } = useCompactNav(location.pathname);
  const railMode = isDesktop && compactPreferred;

  const navGroups = useMemo(() => getNavGroups(isAdmin, isTeamlead), [isAdmin, isTeamlead]);

  // Aktive Gruppe zur aktuellen Route
  const activeGroupId = useMemo(
    () => findActiveGroupId(location.pathname, isAdmin, isTeamlead),
    [location.pathname, isAdmin, isTeamlead]
  );

  // Default: nur die aktive Gruppe (bzw. Start) ist aufgeklappt
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => ({
    [activeGroupId ?? 'start']: true,
  }));

  // Aktive Gruppe automatisch aufklappen, ohne vom Nutzer geöffnete zu schließen
  useEffect(() => {
    if (activeGroupId) {
      setExpandedGroups((prev) => (prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true }));
    }
  }, [activeGroupId]);

  // Verlauf mitschreiben (kanonischer Ziel-Pfad)
  const activeLeafPath = useMemo(() => {
    const match = getAllLeaves(isAdmin, isTeamlead).find((l) => isLeafActive(l.path, location.pathname));
    return match?.path;
  }, [location.pathname, isAdmin, isTeamlead]);

  useEffect(() => {
    if (activeLeafPath) recordRecent(activeLeafPath);
  }, [activeLeafPath, recordRecent]);

  // Globaler Shortcut ⌘K / Strg-K für die Command-Palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Geparkte Submissions
  const { data: parkedData } = useQuery({
    queryKey: ['parkedSubmissions'],
    queryFn: () => api.getSubmissions({ status: 'PARKED' }),
    refetchInterval: 60000,
  });

  const parkedAnalysis = useMemo(() => {
    const parkedReports =
      parkedData?.submissions?.filter(
        (s: Submission) => s.formType === 'ONSITE_SERVICE' && s.status === 'PARKED'
      ) || [];

    const now = new Date();
    const reportsWithWarnings = parkedReports.map((report: Submission) => {
      const parkedAt = report.parkedAt ? new Date(report.parkedAt) : new Date(report.updatedAt);
      const hoursParked = (now.getTime() - parkedAt.getTime()) / (1000 * 60 * 60);
      return { ...report, hoursParked, isOverdue: hoursParked > 8 };
    });

    const hasMultiple = reportsWithWarnings.length > 1;
    const hasOverdue = reportsWithWarnings.some((r: any) => r.isOverdue);

    return {
      reports: reportsWithWarnings,
      count: reportsWithWarnings.length,
      hasMultiple,
      hasOverdue,
      hasWarning: hasMultiple || hasOverdue,
    };
  }, [parkedData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigateToReport = (report: Submission) => {
    setParkedPopoverOpen(false);
    navigate(`/service-report/${report.id}`);
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffMins < 60) return `vor ${diffMins}min`;
    if (diffHours < 24) return `vor ${diffHours}h`;
    return `vor ${Math.floor(diffHours / 24)}d`;
  };

  const {
    data: hubProviderData,
    isError: hubProviderQueryError,
    error: hubProviderQueryErr,
    refetch: refetchHubProvider,
  } = useQuery({
    queryKey: ACTIVE_HUB_QUERY_KEY,
    queryFn: () => api.getHubProvider(),
    staleTime: 60_000,
  });

  const hubProviderFailedNoData = hubProviderQueryError && !hubProviderData;
  const hubProviderErrMessage =
    hubProviderQueryErr instanceof Error
      ? hubProviderQueryErr.message
      : String(hubProviderQueryErr ?? 'Unbekannter Fehler');

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const navTo = (path: string) => {
    navigate(path);
    setOpened(false);
  };

  // Ein einzelnes Nav-Ziel (für Gruppen, Favoriten, Verlauf wiederverwendbar)
  const renderLeaf = (item: NavLeaf, keyPrefix: string) => {
    const active = isLeafActive(item.path, location.pathname);
    const fav = isFavorite(item.path);
    const badgeCount = navBadgeCount(navBadges[item.path]);
    return (
      <NavLink
        key={`${keyPrefix}:${item.path}`}
        className={styles.navLink}
        active={active}
        label={<Text size="sm">{item.label}</Text>}
        leftSection={
          <ThemeIcon
            size="sm"
            variant={active ? 'light' : 'subtle'}
            color={active ? 'orange' : 'gray'}
            radius="sm"
          >
            <item.icon size={15} />
          </ThemeIcon>
        }
        rightSection={
          <Group gap={4} wrap="nowrap">
            <NavCountBadge count={badgeCount} />
            <ActionIcon
              component="div"
              size="sm"
              variant="subtle"
              color={fav ? 'yellow' : 'gray'}
              className={`${styles.favStar} ${fav ? styles.favStarActive : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                void toggleFavorite(item.path);
              }}
              aria-label={fav ? 'Favorit entfernen' : 'Zu Favoriten hinzufügen'}
            >
              {fav ? <IconStarFilled size={13} /> : <IconStar size={13} />}
            </ActionIcon>
          </Group>
        }
        onClick={() => navTo(item.path)}
        variant="subtle"
      />
    );
  };

  // Aktiv-Erkennung für die festen Schnellzugriff-Ziele (berücksichtigt ?tab=…)
  const currentTab = new URLSearchParams(location.search).get('tab') ?? 'mine';
  const isQuickLinkActive = (path: string) => {
    if (path === '/tickets?tab=mine') {
      return location.pathname === '/tickets' && currentTab === 'mine';
    }
    if (path === '/tickets?tab=all') {
      return (
        location.pathname.startsWith('/tickets') &&
        !(location.pathname === '/tickets' && currentTab === 'mine')
      );
    }
    return isLeafActive(path, location.pathname);
  };

  const renderQuickLink = (item: NavLeaf) => {
    const active = isQuickLinkActive(item.path);
    const quickCount = navBadgeCount(navBadges[item.path.split('?')[0]]);
    return (
      <NavLink
        key={`quick:${item.path}`}
        className={styles.navLink}
        active={active}
        label={<Text size="sm">{item.label}</Text>}
        leftSection={
          <ThemeIcon
            size="sm"
            variant={active ? 'light' : 'subtle'}
            color={active ? 'orange' : 'gray'}
            radius="sm"
          >
            <item.icon size={15} />
          </ThemeIcon>
        }
        rightSection={quickCount > 0 ? <NavCountBadge count={quickCount} /> : null}
        onClick={() => navTo(item.path)}
        variant="subtle"
      />
    );
  };

  const renderNavGroup = (group: NavGroup) => {
    const isExpanded = !!expandedGroups[group.id];
    const hasActiveItem = group.id === activeGroupId;
    const GroupIcon = group.icon;

    return (
      <Box key={group.id} className={styles.navGroup}>
        <UnstyledButton
          onClick={() => toggleGroup(group.id)}
          className={`${styles.navGroupHeader} ${hasActiveItem ? styles.navGroupHeaderActive : ''}`}
        >
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Group gap={8} wrap="nowrap">
              <GroupIcon size={15} color={hasActiveItem ? 'var(--fb-orange)' : 'var(--mantine-color-dimmed)'} />
              <Text size="xs" fw={700} tt="uppercase" c={hasActiveItem ? 'orange' : 'dimmed'}>
                {group.label}
              </Text>
            </Group>
            <IconChevronRight
              size={14}
              className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
              color={hasActiveItem ? 'var(--fb-orange)' : undefined}
            />
          </Group>
        </UnstyledButton>

        <Collapse expanded={isExpanded}>
          <Stack gap={2} mt={4}>
            {group.sections.map((section, sIdx) => (
              <Box key={`${group.id}:sec:${sIdx}`}>
                {section.label && (
                  <Text size="10px" fw={600} tt="uppercase" c="dimmed" pl={10} pt={6} pb={2} className={styles.sectionLabel}>
                    {section.label}
                  </Text>
                )}
                {section.items.map((item) => renderLeaf(item, group.id))}
              </Box>
            ))}
          </Stack>
        </Collapse>
      </Box>
    );
  };

  // Favoriten- und Verlaufs-Ziele auflösen
  const favoriteLeaves = favorites
    .map((p) => findLeafByPath(p, isAdmin, isTeamlead))
    .filter((l): l is NavLeaf => !!l && !QUICK_LINK_BASE_PATHS.has(l.path));
  const recentLeaves = recents
    .map((p) => findLeafByPath(p, isAdmin, isTeamlead))
    .filter(
      (l): l is NavLeaf =>
        !!l && !favorites.includes(l.path) && !QUICK_LINK_BASE_PATHS.has(l.path)
    )
    .slice(0, 5);

  return (
    <AppShell
      header={{ height: 'calc(56px + env(safe-area-inset-top, 0px))' }}
      navbar={{ width: railMode ? NAV_RAIL_WIDTH : NAV_WIDTH, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
      // Gilt für Navbar-Breite und Inhaltsabstand gemeinsam — beide gleiten im selben Takt.
      transitionDuration={220}
      transitionTimingFunction="cubic-bezier(0.4, 0, 0.2, 1)"
      className={styles.shell}
    >
      <AppShell.Header className={styles.header}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" gap="xs" style={{ minWidth: 0 }}>
            <Burger
              opened={opened}
              onClick={() => setOpened((o) => !o)}
              hiddenFrom="sm"
              size="sm"
              color={theme.colors.gray[5]}
            />
            <UnstyledButton onClick={() => navigate('/')} className={styles.logo}>
              <img src="/fixbyte-logo-dark-bg.svg" alt="FixByte IT" style={{ height: 32, width: 'auto' }} />
            </UnstyledButton>
            {hubProviderData?.provider && (
              <Tooltip label="Aktives Portal – steuert, woher Tickets geladen werden">
                <Badge
                  size="sm"
                  variant="light"
                  color={hubProviderData.provider === 'weclapp' ? 'teal' : 'blue'}
                  visibleFrom="xs"
                >
                  {hubProviderData.provider === 'weclapp' ? 'Weclapp' : 'ConnectWise'}
                </Badge>
              </Tooltip>
            )}
          </Group>

          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {parkedAnalysis.count > 0 && (
              <Popover
                opened={parkedPopoverOpen}
                onChange={setParkedPopoverOpen}
                position="bottom"
                width="min(360px, 92vw)"
                shadow="lg"
              >
                <Popover.Target>
                  <Tooltip label={`${parkedAnalysis.count} geparkte${parkedAnalysis.count > 1 ? ' Reports' : 'r Report'}`}>
                    <UnstyledButton
                      onClick={() => setParkedPopoverOpen((o) => !o)}
                      className={styles.parkedButton}
                      style={{
                        borderColor: parkedAnalysis.hasWarning
                          ? 'var(--mantine-color-red-6)'
                          : 'var(--mantine-color-yellow-6)',
                      }}
                    >
                      <Group gap="xs">
                        <Indicator
                          processing={parkedAnalysis.hasWarning}
                          color={parkedAnalysis.hasWarning ? 'red' : 'yellow'}
                          size={8}
                        >
                          <ThemeIcon
                            size="xs"
                            variant="light"
                            color={parkedAnalysis.hasWarning ? 'red' : 'yellow'}
                          >
                            {parkedAnalysis.hasWarning ? (
                              <IconAlertCircle size={12} />
                            ) : (
                              <IconPlayerPause size={12} />
                            )}
                          </ThemeIcon>
                        </Indicator>
                        <Text size="xs" fw={500} c={parkedAnalysis.hasWarning ? 'red' : 'yellow'}>
                          {parkedAnalysis.count}
                        </Text>
                      </Group>
                    </UnstyledButton>
                  </Tooltip>
                </Popover.Target>

                <Popover.Dropdown p={0}>
                  <Box p="sm" style={{ borderBottom: '1px solid var(--mantine-color-dark-4)' }}>
                    <Group justify="space-between">
                      <Text size="sm" fw={600}>Geparkte Reports</Text>
                      {parkedAnalysis.hasWarning && (
                        <Badge color="red" size="xs" leftSection={<IconAlertCircle size={10} />}>
                          Achtung
                        </Badge>
                      )}
                    </Group>
                    {parkedAnalysis.hasMultiple && (
                      <Text size="xs" c="red" mt={4}>
                        ⚠️ Mehr als ein aktiver Report!
                      </Text>
                    )}
                    {parkedAnalysis.hasOverdue && !parkedAnalysis.hasMultiple && (
                      <Text size="xs" c="red" mt={4}>
                        ⚠️ Report(s) länger als 8h geparkt!
                      </Text>
                    )}
                  </Box>

                  <Stack gap={0}>
                    {parkedAnalysis.reports.map((report: any) => (
                      <UnstyledButton
                        key={report.id}
                        onClick={() => handleNavigateToReport(report)}
                        className={styles.parkedReportItem}
                        p="sm"
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                            <ThemeIcon
                              size="sm"
                              variant="light"
                              color={report.isOverdue ? 'red' : 'yellow'}
                            >
                              {report.isOverdue ? (
                                <IconAlertCircle size={14} />
                              ) : (
                                <IconPlayerPause size={14} />
                              )}
                            </ThemeIcon>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={500} truncate>
                                {report.cwTicketId
                                  ? `#${report.cwTicketId}`
                                  : report.ticketSummary || 'Neues Ticket'}
                              </Text>
                              <Group gap={4}>
                                <IconClock size={10} color="var(--mantine-color-dimmed)" />
                                <Text size="xs" c={report.isOverdue ? 'red' : 'dimmed'}>
                                  {formatTimeAgo(new Date(report.parkedAt || report.updatedAt))}
                                </Text>
                              </Group>
                            </div>
                          </Group>
                          <IconExternalLink size={14} color="var(--mantine-color-dimmed)" />
                        </Group>
                      </UnstyledButton>
                    ))}
                  </Stack>

                  <Box p="xs" style={{ borderTop: '1px solid var(--mantine-color-dark-4)' }}>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      fullWidth
                      onClick={() => setParkedPopoverOpen(false)}
                    >
                      Schließen
                    </Button>
                  </Box>
                </Popover.Dropdown>
              </Popover>
            )}

            <Tooltip label="Control Cockpit öffnen">
              <Button
                component="a"
                href="https://control-cockpit.fixbyte.it"
                target="_blank"
                rel="noopener noreferrer"
                variant="light"
                color="orange"
                size="xs"
                leftSection={<IconExternalLink size={14} />}
                visibleFrom="sm"
              >
                Cockpit
              </Button>
            </Tooltip>

              <WeclappTokenIndicator />

              <PendingWeclappWorkIndicator />

              <TimeProposalIndicator />

              <AttendanceHeaderButton />

              <OfflineIndicator onManualSync={syncNow} />

            <NotificationBell />

            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton className={styles.userButton}>
                  <Group gap="xs">
                    {/* Die eigene Personenfarbe — dieselbe wie im Kalender, auf Boards und in Listen. */}
                    <Avatar size="sm" radius="xl" color={myColor} variant="filled" autoContrast style={personRingStyle(myColor)}>
                      {user?.displayName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box visibleFrom="sm">
                      <Text size="sm" fw={500} c="white" lh={1.2}>
                        {user?.displayName}
                      </Text>
                      <Text size="xs" c="dimmed" lh={1.2}>
                        {getPortalRoleLabel(user?.role)}
                      </Text>
                    </Box>
                    <IconChevronDown size={14} color={theme.colors.gray[5]} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Eingeloggt als</Menu.Label>
                <Menu.Item disabled>
                  <Text size="sm">{user?.email}</Text>
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item leftSection={<IconUsers size={14} />} onClick={() => navigate('/profile')}>
                  Mein Profil
                </Menu.Item>
                <Menu.Item leftSection={<IconSparkles size={14} />} onClick={openChangelog}>
                  Was ist neu
                </Menu.Item>
                <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={handleLogout}>
                  Abmelden
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar className={styles.navbar} p="xs">
        {railMode ? (
          <CompactNavRail
            pathname={location.pathname}
            quickLinks={QUICK_LINKS}
            isQuickLinkActive={isQuickLinkActive}
            favoriteLeaves={favoriteLeaves}
            recentLeaves={recentLeaves}
            navGroups={navGroups}
            activeGroupId={activeGroupId}
            navBadges={navBadges}
            hubError={hubProviderFailedNoData ? hubProviderErrMessage : null}
            onRetryHub={() => void refetchHubProvider()}
            onNavigate={navTo}
            onOpenSearch={() => setPaletteOpen(true)}
            onOpenChangelog={openChangelog}
            onExpand={toggleCompact}
          />
        ) : (
          <>
            <ScrollArea className={styles.navScroll} scrollbarSize={6}>
              <Stack gap="md">
                {/* Suche / Command-Palette */}
                <UnstyledButton className={styles.searchTrigger} onClick={() => setPaletteOpen(true)}>
                  <Group gap="xs" wrap="nowrap">
                    <IconSearch size={15} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                      Suchen …
                    </Text>
                    <Kbd size="xs">⌘K</Kbd>
                  </Group>
                </UnstyledButton>

                {/* Quick Actions — mobil nebeneinander (der Drawer ist dort volle Breite),
                    im schmalen Desktop-Navbar untereinander */}
                <Box className={styles.quickActions}>
                  <SimpleGrid cols={{ base: 3, sm: 1 }} spacing="xs">
                    {NAV_QUICK_ACTIONS.map((a) => (
                      <Tooltip key={a.path} label={a.tooltip}>
                        <Button
                          variant="light"
                          color={a.color}
                          size="xs"
                          leftSection={<a.icon size={14} />}
                          onClick={() => navTo(a.path)}
                          className={styles.quickActionButton}
                        >
                          {a.label}
                        </Button>
                      </Tooltip>
                    ))}
                  </SimpleGrid>
                </Box>

                {hubProviderFailedNoData && (
                  <Alert
                    icon={<IconAlertTriangle size={16} />}
                    color="yellow"
                    variant="light"
                    title="Hub-Einstellungen"
                    p="xs"
                  >
                    <Text size="xs" mb="xs">
                      Konnten nicht geladen werden: {hubProviderErrMessage}
                    </Text>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconRefresh size={14} />}
                      onClick={() => void refetchHubProvider()}
                    >
                      Erneut versuchen
                    </Button>
                  </Alert>
                )}

                {/* Schnellzugriff – immer sichtbar */}
                <Box className={styles.pinnedSection}>
                  <Group gap={8} pl={4} mb={4}>
                    <IconBolt size={13} color="var(--fb-orange)" />
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                      Schnellzugriff
                    </Text>
                  </Group>
                  <Stack gap={2}>{QUICK_LINKS.map(renderQuickLink)}</Stack>
                </Box>

                {/* Favoriten */}
                {favoriteLeaves.length > 0 && (
                  <Box className={styles.pinnedSection}>
                    <Group gap={8} pl={4} mb={4}>
                      <IconStarFilled size={13} color="var(--mantine-color-yellow-5)" />
                      <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                        Favoriten
                      </Text>
                    </Group>
                    <Stack gap={2}>{favoriteLeaves.map((item) => renderLeaf(item, 'fav'))}</Stack>
                  </Box>
                )}

                {/* Zuletzt benutzt */}
                {recentLeaves.length > 0 && (
                  <Box className={styles.pinnedSection}>
                    <Group gap={8} pl={4} mb={4}>
                      <IconHistory size={13} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                        Zuletzt benutzt
                      </Text>
                    </Group>
                    <Stack gap={2}>{recentLeaves.map((item) => renderLeaf(item, 'recent'))}</Stack>
                  </Box>
                )}

                <Divider />

                {/* Nav-Gruppen */}
                {navGroups.map(renderNavGroup)}
              </Stack>
            </ScrollArea>

            <Box className={styles.navFooter}>
              <Divider mb="xs" />
              <Group gap={4} wrap="nowrap">
                <Text size="xs" ta="center" style={{ flex: 1, minWidth: 0 }}>
                  {/* Versionsnummer als Einstieg in die Release-Notes — dort sucht man sie. */}
                  <Tooltip label="Was ist neu?" withArrow>
                    <Text
                      span
                      size="xs"
                      fw={600}
                      style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                      onClick={openChangelog}
                    >
                      v{__APP_VERSION__}
                    </Text>
                  </Tooltip>
                  <Text span size="xs" c="dimmed">
                    {' '}• © 2026 FixByte IT
                  </Text>
                </Text>
                {isDesktop ? (
                  <Tooltip label="Menü einklappen" position="right" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={toggleCompact} aria-label="Menü einklappen">
                      <IconLayoutSidebarLeftCollapse size={16} />
                    </ActionIcon>
                  </Tooltip>
                ) : null}
              </Group>
            </Box>
          </>
        )}
      </AppShell.Navbar>

      <ConflictResolutionDialog />
      <SyncErrorDialog />
      <CommandPalette opened={paletteOpen} onClose={() => setPaletteOpen(false)} isAdmin={isAdmin} isTeamlead={isTeamlead} />

      <AppShell.Main className={styles.main} pb={{ base: 72, sm: 0 }}>
        <OfflineInfoModal />
        <ChangelogModal />
        <TodoInboxModal />
        <div className="fade-in">
          <ViewAsBanner />
          <CustomerSsoSetupBanner />
          <MailIntakeSetupBanner />
          <Outlet />
        </div>
      </AppShell.Main>

      <Box hiddenFrom="sm">
        <BottomNav />
      </Box>

      {/* Läuft über allen Seiten, damit der Rundgang beim Navigieren nicht abreißt. */}
      <GuidedTour />
    </AppShell>
  );
}
